/**
 * Leave (Verlof) Types
 *
 * Shared types for the verlof-selfservice feature (Spoor 2, Verlof MVP).
 * Used by both frontend and backend.
 *
 * AD-1 (architecture spine): `requestStatus` is the workflow-approval axis,
 * orthogonal to the existing Nmbrs `syncStatus` (booking) axis on
 * `nmbrs_leave_requests` — the two are never conflated.
 */
/**
 * FR-1 — de verlofgroep waar een verlofsoort bij hoort: Nmbrs-type 3,
 * *Kort verzuim + Bijz.verlof*. Bij de andere drie hoort er geen soort, ook
 * niet ná de ingangsdatum.
 *
 * Hij staat in `packages/shared` omdat de dienst, het indienschema en twee
 * schermen dezelfde vraag stellen. Vier losse `= 3`-regels is de vorm die uit
 * elkaar loopt zodra Nmbrs ooit hernummert (KE-9). Gecentraliseerd bij de
 * code review van 2026-08-17.
 */
export const VERLOFGROEP_KORT_VERZUIM = 3;
/**
 * Everything the rest of the codebase needs to know about a verlofsoort, on
 * ONE table (AD-27).
 *
 * `boektInLoonadministratie` is the ONLY place in the codebase that records
 * which soort is never booked (FR-22, KE-9). Not in a service, not in a
 * screen, not "temporarily". A second non-booking soort therefore costs one
 * line here and nothing anywhere else — which is precisely the property FR-22
 * asks for. Anything that needs the answer asks `soortBoektNooit(soort)`.
 *
 * The table is pure: no database, no setting, no import from the backend. It
 * is data about the domain, not about a deployment.
 */
export const VERLOFSOORTEN = {
    medisch_bezoek: {
        boektInLoonadministratie: true,
        teltMeeInMaandruimte: true,
        hrStapAltijd: false,
        leidinggevendeBeslist: true,
    },
    bijzonder_verlof: {
        boektInLoonadministratie: true,
        teltMeeInMaandruimte: false,
        hrStapAltijd: true,
        leidinggevendeBeslist: true,
    },
    calamiteiten_of_zorgverlof: {
        boektInLoonadministratie: false,
        teltMeeInMaandruimte: false,
        hrStapAltijd: true,
        leidinggevendeBeslist: false,
    },
};
/**
 * The soorten that never reach the loonadministratie — DERIVED from
 * `VERLOFSOORTEN`, never written out by hand (AD-27). Two lists that say the
 * same thing drift apart; that is exactly what this constant must not become.
 */
export const NIET_BOEKENDE_SOORTEN = Object.keys(VERLOFSOORTEN).filter((soort) => VERLOFSOORTEN[soort].boektInLoonadministratie === false);
/**
 * FR-4 — de soorten die de maandruimte verbruiken, DERIVED uit `VERLOFSOORTEN`
 * op precies dezelfde manier als `NIET_BOEKENDE_SOORTEN` hierboven (AD-27,
 * KE-9).
 *
 * Het maandruimte-eindpunt (story 6.2) heeft deze lijst nodig om de aanvraag
 * IN WORDING als extra regel aan de teller mee te geven: die aanvraag bestaat
 * nog niet als rij, en zonder soort telt `teltMeeInMaandteller` hem niet mee.
 * Een soortnaam met de hand in dat eindpunt schrijven zou de tweede lijst zijn
 * die KE-9 uitsluit.
 */
export const MAANDRUIMTE_SOORTEN = Object.keys(VERLOFSOORTEN).filter((soort) => VERLOFSOORTEN[soort].teltMeeInMaandruimte);
/**
 * *Beslist HR als enige over deze soort?* — HET predicaat, en de enige plek in
 * de hele codebase waar `VERLOFSOORTEN[...].leidinggevendeBeslist` wordt
 * gelezen (AD-27, KE-9, code review 2026-08-17).
 *
 * WAAROM DIT ER IS. Dezelfde vraag stond op drie plekken los uitgeschreven:
 * `goedkeuringsroute` (de route over één rij), `ALLEEN_HR_REDENEN` (dezelfde
 * regel binnen een `WHERE`) en `leidinggevendeBeslistNiet` in het scherm — die
 * laatste onder een andere naam, waardoor de greptest van story 8.2 AC-9 er
 * overheen gleed. Drie formuleringen van één regel lopen uiteen zodra er één
 * verandert; nu leunen ze alle drie hierop.
 *
 * ONBEKEND EN LEEG ZIJN GEEN *alleen HR*. Een reden die géén soort is (*boven
 * de maandruimte*) staat niet in de tabel, en een lege waarde betekent dat er
 * geen HR-stap is: in beide gevallen beslist de leidinggevende gewoon als
 * eerste (FR-19). Wie de omgekeerde vraag stelt — *mag de leidinggevende
 * meekijken?* — heeft aan deze functie NIET genoeg: daar gaat een onbekende
 * soort juist dicht in plaats van open, en die afweging staat bij die eigen
 * poort.
 */
export function beslistAlleenHr(redenOfSoort) {
    if (redenOfSoort == null)
        return false;
    const kenmerken = VERLOFSOORTEN[redenOfSoort];
    return kenmerken !== undefined && !kenmerken.leidinggevendeBeslist;
}
/**
 * De soorten waarbij HR de ENIGE beslisser is (route `alleen_hr`, besluit
 * V21), DERIVED uit `VERLOFSOORTEN` op precies dezelfde manier als de twee
 * lijsten hierboven (AD-27, KE-9) — en via {@link beslistAlleenHr}, zodat de
 * regel zelf hier niet voor de tweede keer staat.
 *
 * Dit is de SQL-helft van `goedkeuringsroute`: die functie is de leesbare
 * poort over één rij, deze lijst maakt diezelfde regel toetsbaar binnen een
 * `WHERE` — de leesconditie van de aanwezigheidskalender (story 8.3, AD-32) en
 * de conditionele update van het HR-besluit (story 7.3) hebben er allebei één
 * nodig. Een reden die een soort noemt draagt de spelling van die soort, dus
 * de sleutels van deze tabel ZIJN de waarden waar `hr_stap_reden` op staat.
 */
export const ALLEEN_HR_REDENEN = Object.keys(VERLOFSOORTEN).filter((soort) => beslistAlleenHr(soort));
/**
 * FR-21/FR-22 — does this soort never reach the loonadministratie?
 *
 * Accepts an absent or unknown value and answers `false` for it (story 3.1,
 * AC-17): plain leave books, and plain leave is what every request that
 * predates the verlofsoorten work is (FR-19). Callers read this off a DTO
 * field that is optional by design, so forcing them to narrow first would only
 * spread the same guard over every call site — and a guard that lives in two
 * places is the second definition KE-9 forbids.
 */
export function soortBoektNooit(soort) {
    if (soort == null)
        return false;
    const kenmerken = VERLOFSOORTEN[soort];
    return kenmerken ? kenmerken.boektInLoonadministratie === false : false;
}
/**
 * FR-4 — does this soort consume the monthly allowance?
 *
 * Accepts an absent or unknown value and answers `false` for it, exactly like
 * `soortBoektNooit` above: leave without a soort — everything that predates
 * this work, everything read back from Nmbrs, and every other verlofgroep —
 * has no monthly allowance to consume (FR-19). Story 6.1 reads this straight
 * off a row whose `verlofsoort` column is nullable by design, so forcing the
 * caller to narrow first would only spread the same guard over every call
 * site — and a guard in two places is the second definition KE-9 forbids.
 */
export function soortTeltMeeInMaandruimte(soort) {
    if (soort == null)
        return false;
    const kenmerken = VERLOFSOORTEN[soort];
    return kenmerken ? kenmerken.teltMeeInMaandruimte : false;
}
/**
 * Story 7.3, AC-1 — the words a screen shows for an HR-step reason (§10.5,
 * besluit V25). Verbatim microcopy, and the ONLY place it lives: the reason
 * chip, the split counter above the list and every later screen read the same
 * three strings, so they can never drift into three spellings (KE-7, KE-11).
 *
 * It sits in this file for the same reason `VERLOFSOORTEN` does: this is where
 * the vocabulary of the domain lives, and a screen that maps a reason to words
 * itself would be a second definition (KE-9).
 */
export const HR_STAP_REDEN_LABELS = {
    boven_maandruimte: 'Boven de 3 uur',
    bijzonder_verlof: 'Bijzonder verlof',
    calamiteiten_of_zorgverlof: 'Calamiteiten- of zorgverlof',
};
/**
 * The label for a reason, `null` when there is no HR step.
 *
 * A reason this build does not know yet still reads as TEXT rather than as an
 * empty chip (KE-3): the fallback turns the stored word into a sentence
 * ("boven_de_3_uur" → "Boven de 3 uur"). That matters because `hr_stap_reden`
 * is a `varchar` filled by a write path that a later story may extend — an
 * unknown value must degrade to something a screen reader can announce, never
 * to a blank.
 */
export function hrStapRedenLabel(reden) {
    if (reden == null)
        return null;
    const bekend = HR_STAP_REDEN_LABELS[reden];
    if (bekend)
        return bekend;
    const woorden = reden.replace(/_/g, ' ').trim();
    return woorden.charAt(0).toUpperCase() + woorden.slice(1);
}
/**
 * Story 7.4, AC-1/AC-5 — the words the historie uses for a soort (§10.6,
 * §10.8, besluit V25). Verbatim microcopy, one place, exactly like
 * `HR_STAP_REDEN_LABELS` above: the historie of the medewerker, of the
 * leidinggevende and of HR read the same strings, so they can never drift
 * into three spellings (KE-7, KE-11).
 *
 * The calamiteiten line carries its own tail — *"gaat niet naar de
 * loonadministratie"* is part of the vastgestelde regel in §10.8, not an
 * addition made at the point of use.
 */
export const VERLOFSOORT_HISTORIE_TEKSTEN = {
    medisch_bezoek: 'medisch bezoek onder werktijd',
    bijzonder_verlof: 'bijzonder verlof',
    calamiteiten_of_zorgverlof: 'calamiteiten- of zorgverlof — gaat niet naar de loonadministratie',
};
/**
 * The historie wording for a soort, `null` when the request has none (all
 * leave that predates this work — FR-19).
 *
 * A soort this build does not know yet still reads as TEXT rather than as a
 * blank line (KE-3): the fallback turns the stored word into words
 * ("bijzonder_verlof" → "bijzonder verlof"), the same degradation
 * `hrStapRedenLabel` makes.
 */
export function verlofsoortHistorieTekst(soort) {
    if (soort == null)
        return null;
    const bekend = VERLOFSOORT_HISTORIE_TEKSTEN[soort];
    if (bekend)
        return bekend;
    return soort.replace(/_/g, ' ').trim();
}
/**
 * Story 5.5, AC-9 (besluit V42) — de KORTE naam van een soort: alleen hoe de
 * soort heet, zonder de toelichting die §10.6/§10.8 achter de regel *Soort:
 * …* zetten.
 *
 * **Waarom dit naast `VERLOFSOORT_HISTORIE_TEKSTEN` staat en niet in plaats
 * daarvan.** Die tabel draagt de vastgestelde regel van het INDIENEN, staart
 * en al: *"Soort: calamiteiten- of zorgverlof — gaat niet naar de
 * loonadministratie"* is één zin uit §10.8. Besluit V42 legt voor de
 * CORRECTIE een andere zin vast — *"Soort aangepast door HR (Petra) van
 * medisch bezoek naar calamiteiten- en zorgverlof op 12 augustus 09:14"* —
 * waarin alleen de naam en het tijdstip worden ingevuld. Zou die zin de
 * tekst hierboven lenen, dan breekt de toelichtende staart hem doormidden en
 * loopt de regel dood op *"… naar calamiteiten- of zorgverlof — gaat niet
 * naar de loonadministratie op 12 augustus 09:14"*.
 *
 * Twee vastgestelde zinnen, twee bewoordingen — allebei letterlijk, allebei
 * op één plek (KE-7). Gevonden bij de code review van 2026-08-17.
 */
export const VERLOFSOORT_KORTE_NAMEN = {
    medisch_bezoek: 'medisch bezoek',
    bijzonder_verlof: 'bijzonder verlof',
    calamiteiten_of_zorgverlof: 'calamiteiten- en zorgverlof',
};
/**
 * De korte soortnaam, `null` wanneer de aanvraag er geen draagt — dezelfde
 * vorm en dezelfde terugval als {@link verlofsoortHistorieTekst}: een soort
 * die deze build niet kent leest nog steeds als woorden en nooit als een lege
 * plek (KE-3).
 */
export function verlofsoortKorteNaam(soort) {
    if (soort == null)
        return null;
    const bekend = VERLOFSOORT_KORTE_NAMEN[soort];
    if (bekend)
        return bekend;
    return soort.replace(/_/g, ' ').trim();
}
/**
 * Story 10.1, AC-1 — DE rechtentabel van het handboek (§10.3, FR-10, AD-38).
 *
 * Dit is de ENIGE plek in de codebase waar het handboek staat. Een tweede
 * lijst met dezelfde teksten of dezelfde getallen — in een dienst, in een
 * scherm, in een schema — is precies wat AD-38 uitsluit; alles wat het
 * antwoord nodig heeft leest deze tabel.
 *
 * DE VOLGORDE IS DIE VAN HET HANDBOEK (FR-10) en is onderdeel van het
 * contract: de keuzelijst in het formulier loopt hem in deze volgorde af.
 *
 * TIEN GEBEURTENISSEN, GEEN ELF. Partnerverlof (geboorteverlof) staat er niet
 * in (besluit V23) en is niet als bijzonder verlof aan te vragen; er is ook
 * geen gebeurtenis *overig* — wat hier niet staat, is geen bijzonder verlof.
 *
 * DE TABEL IS EEN VOORSTEL EN NOOIT EEN GRENS (AD-38, besluit V12): een
 * toegekend aantal dat ervan afwijkt is geen fout en nooit een waarschuwing.
 * Er bestaat dan ook nergens een foutcode die zo'n afwijking weigert.
 */
export const BIJZONDER_VERLOF_GEBEURTENISSEN = {
    ondertrouw: {
        tekst: 'Ondertrouw of registratie van partnerschap — 1 dag',
        werkdagen: 1,
        isOverlijden: false,
        vastAantalDagen: true,
    },
    eigen_huwelijk: {
        tekst: 'Eigen huwelijk — 2 dagen',
        werkdagen: 2,
        isOverlijden: false,
        vastAantalDagen: true,
    },
    huwelijk_familie: {
        tekst: 'Huwelijk van je kind, ouder, broer, zus, schoonouder, zwager of schoonzus — 1 dag',
        werkdagen: 1,
        isOverlijden: false,
        vastAantalDagen: true,
    },
    overlijden_partner_of_kind: {
        tekst: 'Overlijden van je partner of (aangehuwd) kind — van de dag van overlijden tot en met de begrafenis of crematie, maximaal 5 werkdagen',
        werkdagen: 5,
        isOverlijden: true,
        vastAantalDagen: false,
    },
    overlijden_ouder: {
        tekst: 'Overlijden van je ouder, broer, zus, pleegouder, schoonouder of niet-inwonend kind — van de dag van overlijden tot en met de begrafenis of crematie, maximaal 4 werkdagen',
        werkdagen: 4,
        isOverlijden: true,
        vastAantalDagen: false,
    },
    overlijden_grootouder_of_kleinkind: {
        tekst: 'Overlijden van je grootouder of kleinkind — de dag van overlijden en de dag van de begrafenis of crematie',
        werkdagen: 2,
        isOverlijden: true,
        vastAantalDagen: false,
    },
    overlijden_overige_familie: {
        tekst: 'Overlijden van je zwager, schoonzus, tante, oom of achterkleinkind — de dag van de begrafenis of crematie',
        werkdagen: 1,
        isOverlijden: true,
        vastAantalDagen: false,
    },
    jubileum_eigen: {
        tekst: '25-, 40- of 50-jarig huwelijksfeest of dienstjubileum — 1 dag',
        werkdagen: 1,
        isOverlijden: false,
        vastAantalDagen: true,
    },
    jubileum_ouders: {
        tekst: '25-, 40-, 50- of 60-jarig huwelijksjubileum van je ouders — 1 dag',
        werkdagen: 1,
        isOverlijden: false,
        vastAantalDagen: true,
    },
    verhuizing: {
        tekst: 'Verhuizing — 1 dag, maximaal 1 keer per jaar',
        werkdagen: 1,
        isOverlijden: false,
        vastAantalDagen: true,
    },
};
/**
 * De sleutels in de volgorde van het handboek (FR-10) — AFGELEID, zodat er
 * geen tweede lijst naast de tabel komt te staan.
 */
export const BIJZONDER_VERLOF_GEBEURTENIS_WAARDEN = Object.keys(BIJZONDER_VERLOF_GEBEURTENISSEN);
/**
 * *Gaat het hier om bijzonder verlof?* — HET predicaat, en de enige plek buiten
 * `VERLOFSOORTEN` hierboven waar die soortnaam letterlijk voorkomt (KE-9,
 * AD-27).
 *
 * WAAROM DIT ER IS. Story 10.1 tot en met 10.4 hangen aan één soort: de
 * gebeurteniskeuze verschijnt daar, de uitkomstregel hoort daarbij, en de
 * waarschuwingszin voor de leidinggevende gaat erover. Zonder dit predicaat zou
 * elk van die vier plekken een eigen `=== 'bijzonder_verlof'` krijgen — precies
 * de vorm die uiteenloopt zodra er iets aan de tabel verandert, en die de
 * greptests van de stories 5.1, 5.2, 7.1, 7.3 en 9.1 uitsluiten.
 *
 * Hij neemt zowel een SOORT als een HR-STAP-REDEN aan, net als
 * {@link beslistAlleenHr}: een reden die een soort noemt draagt diens eigen
 * spelling, dus de waarde ís de sleutel die is opgezocht.
 */
export function isBijzonderVerlof(soortOfReden) {
    return soortOfReden === 'bijzonder_verlof';
}
/** Kent het handboek deze gebeurtenis? Wat hier niet staat is geen bijzonder verlof. */
export function isBijzondereGebeurtenis(waarde) {
    return waarde != null && Object.hasOwn(BIJZONDER_VERLOF_GEBEURTENISSEN, waarde);
}
/** De regel bij een gebeurtenis, of `null` wanneer het handboek hem niet kent. */
export function bijzondereGebeurtenisRegel(gebeurtenis) {
    if (!isBijzondereGebeurtenis(gebeurtenis))
        return null;
    return BIJZONDER_VERLOF_GEBEURTENISSEN[gebeurtenis];
}
/**
 * Het handboekmaximum in werkdagen, of `null` wanneer er geen (bekende)
 * gebeurtenis is. HET ENIGE antwoord op *"hoeveel dagen noemt het handboek
 * hier?"*, gelezen door het formulier én door de HR-lijst (AD-38, KE-9).
 */
export function bijzondereGebeurtenisMaximum(gebeurtenis) {
    return bijzondereGebeurtenisRegel(gebeurtenis)?.werkdagen ?? null;
}
/**
 * De gebeurtenis in woorden, voor een scherm dat haar naast de aanvraag zet
 * (KE-3: tekst, nooit een sleutel). Een gebeurtenis die deze build niet kent
 * leest nog steeds als woorden en nooit als een lege plek — dezelfde terugval
 * als {@link verlofsoortKorteNaam}.
 */
export function bijzondereGebeurtenisNaam(gebeurtenis) {
    if (gebeurtenis == null)
        return null;
    return gebeurtenis.replace(/_/g, ' ').trim();
}
/**
 * De historie-regel voor een bijzondere gebeurtenis (§10.6), of `null` als er
 * geen gebeurtenis is.
 *
 * *"Gebeurtenis: overlijden ouder — handboek noemt maximaal 4 werkdagen"* —
 * alleen de naam en het getal worden ingevuld. Een gebeurtenis die deze
 * build niet kent verliest de staart en houdt de naam; hij wordt nooit een
 * lege regel.
 *
 * Story 10.1: het getal komt sinds deze story uit de rechtentabel hierboven,
 * die de losse maximatabel van story 7.4 heeft opgeslokt. De uitkomst van deze
 * functie is daarbij ONVERANDERD gebleven — dat is de regressie die bewijst
 * dat de historie van story 7.4 heel is.
 */
export function bijzondereGebeurtenisTekst(gebeurtenis) {
    if (gebeurtenis == null)
        return null;
    const naam = bijzondereGebeurtenisNaam(gebeurtenis);
    const maximum = bijzondereGebeurtenisMaximum(gebeurtenis);
    if (maximum === null)
        return naam;
    return `${naam} — handboek noemt maximaal ${maximum} werkdagen`;
}
/**
 * Story 2.8, AC-7 — de eindreden voor verlof dat wél geboekt is maar geen
 * kenmerk draagt, met de bijbehorende foutcode. Eén plek, zodat epic 4 (het
 * HR-scherm) en de dienstlaag dezelfde tekst en dezelfde code gebruiken.
 */
export const NMBRS_LEAVE_ID_ONBEKEND = 'NMBRS_LEAVE_ID_ONBEKEND';
export const REDEN_GEBOEKT_ZONDER_KENMERK = 'geboekt zonder kenmerk';
/**
 * All five codes, in the order AC-4 lists them. Used to recognise a value that
 * arrived from outside the type system (a database column, an API payload)
 * before it is trusted; anything else falls back to the safe lower bound —
 * see {@link VERLOF_KOPPEL_REDENEN_OPNIEUW_TE_PROBEREN}.
 */
export const VERLOF_KOPPEL_REDENEN = [
    'geen_overeenkomende_regel',
    'meerdere_gelijke_regels',
    'loonadministratie_onbereikbaar',
    'waarden_gewijzigd_na_boeking',
    'geboekt_zonder_kenmerk',
];
/**
 * FR-7 — the ONLY reason worth another attempt. The other four have no chance
 * of succeeding: there is nothing to match, there is more than one match, the
 * values changed after booking, or the hours are already in the
 * loonadministratie without a kenmerk. Offering HR a button for those costs
 * time and yields nothing.
 *
 * A screen never derives this from the reason text; it reads the flag the
 * classification puts on the row.
 */
export const VERLOF_KOPPEL_REDENEN_OPNIEUW_TE_PROBEREN = [
    'loonadministratie_onbereikbaar',
];
/**
 * The TOTAL mapping from what epic 2 stores to what epic 4 compares against.
 *
 * Epic 2 is live and writes {@link KoppelReden} into the audit trail; this
 * table is the only place that translates those values, so a sixth reason
 * costs one line here and nothing anywhere else. `'gekoppeld'` maps to `null`
 * on purpose: such a row has a kenmerk and therefore never lands in the
 * "niet gekoppeld" group at all.
 */
export const KOPPEL_REDEN_ALS_VERLOF_KOPPEL_REDEN = {
    gekoppeld: null,
    'geen overeenkomende regel gevonden': 'geen_overeenkomende_regel',
    'meerdere gelijke regels gevonden': 'meerdere_gelijke_regels',
    'de loonadministratie was niet bereikbaar': 'loonadministratie_onbereikbaar',
    'waarden gewijzigd na boeking': 'waarden_gewijzigd_na_boeking',
    'geboekt zonder kenmerk': 'geboekt_zonder_kenmerk',
};
//# sourceMappingURL=leave.js.map